import {
  type AbstractMesh,
  Color4,
  DynamicTexture,
  ParticleSystem,
  Scene,
  Texture,
  Vector3,
} from '@babylonjs/core';

/** Resolver: ground-cover color (RGB 0..1) under a world XZ position, or null off-grid. */
export type CoverSampler = (worldX: number, worldZ: number) => [number, number, number] | null;

/**
 * Per-vehicle particle systems: engine-start puff, off-road wheel dirt (color-tinted to
 * the underlying terrain cover), and crash dust.
 *
 * All three share a single baked-once "puff" DynamicTexture so we don't pay for multiple
 * cloud sprites. Particle systems are CPU-side (Babylon's `ParticleSystem`) — counts and
 * lifetimes are low enough that CPU work is negligible.
 */
export class VehicleFx {
  private scene: Scene;
  private texture: Texture;

  private startBurst: ParticleSystem;
  private wheelDirt: ParticleSystem[] = [];
  private crashDust: ParticleSystem;

  /** Cooldown gate so a single crash doesn't spam particles every frame. */
  private crashCooldown = 0;

  constructor(scene: Scene, chassisRoot: AbstractMesh, wheelNodes: AbstractMesh[]) {
    this.scene = scene;
    this.texture = makePuffTexture(scene);

    this.startBurst = makeStartBurst(scene, this.texture, chassisRoot);
    for (const w of wheelNodes) {
      this.wheelDirt.push(makeWheelDirt(scene, this.texture, w));
    }
    this.crashDust = makeCrashDust(scene, this.texture, chassisRoot);
  }

  /** Fire the engine-start burst (call on spawn / respawn). */
  triggerStart(): void {
    this.startBurst.manualEmitCount = 30;
    if (!this.startBurst.isStarted()) this.startBurst.start();
  }

  /** Fire crash dust at the chassis (call when a high-impulse impact is detected). */
  triggerCrash(): void {
    if (this.crashCooldown > 0) return;
    this.crashCooldown = 0.5;
    this.crashDust.manualEmitCount = 60;
    if (!this.crashDust.isStarted()) this.crashDust.start();
  }

  /**
   * Per-frame update. `wheelGround[i]` is true if wheel i is touching ground;
   * `wheelSpins[i]` is angular speed in rad/s; `coverAt` looks up the terrain color.
   */
  update(
    dt: number,
    _forwardSpeed: number,
    wheelWorldPositions: Vector3[],
    wheelGround: boolean[],
    wheelSpins: number[],
    coverAt: CoverSampler,
  ): void {
    this.crashCooldown = Math.max(0, this.crashCooldown - dt);

    // Wheel dirt: emit if grounded + spinning + over non-road cover.
    for (let i = 0; i < this.wheelDirt.length; i++) {
      const ps = this.wheelDirt[i]!;
      const pos = wheelWorldPositions[i];
      const grounded = wheelGround[i] ?? false;
      const spin = Math.abs(wheelSpins[i] ?? 0);
      if (!grounded || spin < 1 || !pos) {
        ps.emitRate = 0;
        continue;
      }
      const cover = coverAt(pos.x, pos.z);
      if (!cover || isRoadColor(cover)) {
        ps.emitRate = 0;
        continue;
      }
      const a = 0.85;
      ps.color1 = new Color4(cover[0], cover[1], cover[2], a);
      ps.color2 = new Color4(cover[0] * 0.7, cover[1] * 0.7, cover[2] * 0.7, a);
      ps.colorDead = new Color4(cover[0] * 0.5, cover[1] * 0.5, cover[2] * 0.5, 0);
      ps.emitRate = Math.min(80, 8 + spin * 4);
      if (!ps.isStarted()) ps.start();
    }
  }

  dispose(): void {
    this.startBurst.dispose();
    for (const w of this.wheelDirt) w.dispose();
    this.crashDust.dispose();
    this.texture.dispose();
  }
}

// --- Particle system factories ----------------------------------------------

function makeStartBurst(scene: Scene, tex: Texture, parent: AbstractMesh): ParticleSystem {
  const ps = new ParticleSystem('fx-start', 100, scene);
  ps.particleTexture = tex;
  ps.emitter = parent;
  ps.minEmitBox = new Vector3(-0.4, 0.05, -1.4);
  ps.maxEmitBox = new Vector3(0.4, 0.25, -1.0);
  ps.color1 = new Color4(0.95, 0.95, 1.0, 0.9);
  ps.color2 = new Color4(0.7, 0.85, 1.0, 0.9);
  ps.colorDead = new Color4(1, 1, 1, 0);
  ps.minSize = 0.25;
  ps.maxSize = 0.5;
  ps.minLifeTime = 0.5;
  ps.maxLifeTime = 1.0;
  ps.emitRate = 0;
  ps.gravity = new Vector3(0, 0.5, 0);
  ps.direction1 = new Vector3(-0.5, 0.6, -1.5);
  ps.direction2 = new Vector3(0.5, 1.0, -2.5);
  ps.minEmitPower = 1.0;
  ps.maxEmitPower = 2.0;
  ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  return ps;
}

function makeWheelDirt(scene: Scene, tex: Texture, wheel: AbstractMesh): ParticleSystem {
  const ps = new ParticleSystem('fx-wheel-dirt', 150, scene);
  ps.particleTexture = tex;
  ps.emitter = wheel;
  ps.minEmitBox = new Vector3(-0.1, 0, -0.1);
  ps.maxEmitBox = new Vector3(0.1, 0.1, 0.1);
  ps.color1 = new Color4(0.5, 0.7, 0.4, 0.9); // overridden per-frame
  ps.color2 = new Color4(0.3, 0.5, 0.2, 0.9);
  ps.colorDead = new Color4(0.3, 0.4, 0.2, 0);
  ps.minSize = 0.08;
  ps.maxSize = 0.22;
  ps.minLifeTime = 0.3;
  ps.maxLifeTime = 0.7;
  ps.emitRate = 0;
  ps.gravity = new Vector3(0, -9, 0);
  ps.direction1 = new Vector3(-1, 1.5, -1);
  ps.direction2 = new Vector3(1, 2.5, -2);
  ps.minEmitPower = 1.5;
  ps.maxEmitPower = 3.5;
  ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  return ps;
}

function makeCrashDust(scene: Scene, tex: Texture, parent: AbstractMesh): ParticleSystem {
  const ps = new ParticleSystem('fx-crash', 200, scene);
  ps.particleTexture = tex;
  ps.emitter = parent;
  ps.minEmitBox = new Vector3(-0.5, 0, -0.5);
  ps.maxEmitBox = new Vector3(0.5, 0.4, 0.5);
  ps.color1 = new Color4(0.85, 0.82, 0.78, 0.9);
  ps.color2 = new Color4(0.65, 0.62, 0.58, 0.9);
  ps.colorDead = new Color4(0.5, 0.5, 0.5, 0);
  ps.minSize = 0.3;
  ps.maxSize = 0.7;
  ps.minLifeTime = 0.7;
  ps.maxLifeTime = 1.2;
  ps.emitRate = 0;
  ps.gravity = new Vector3(0, -4, 0);
  ps.direction1 = new Vector3(-3, 1, -3);
  ps.direction2 = new Vector3(3, 3, 3);
  ps.minEmitPower = 2.0;
  ps.maxEmitPower = 5.0;
  ps.blendMode = ParticleSystem.BLENDMODE_STANDARD;
  return ps;
}

// --- Texture ----------------------------------------------------------------

/** A simple chunky-puff sprite: a centered radial gradient. Baked once. */
function makePuffTexture(scene: Scene): DynamicTexture {
  const size = 64;
  const tex = new DynamicTexture('fx-puff-tex', { width: size, height: size }, scene, false);
  const ctx = tex.getContext() as CanvasRenderingContext2D;
  ctx.clearRect(0, 0, size, size);
  const cx = size * 0.5;
  const cy = size * 0.5;
  const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, size * 0.5);
  g.addColorStop(0, 'rgba(255,255,255,1)');
  g.addColorStop(0.5, 'rgba(255,255,255,0.7)');
  g.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(cx, cy, size * 0.5, 0, Math.PI * 2);
  ctx.fill();
  tex.hasAlpha = true;
  tex.update(false);
  return tex;
}

/**
 * Heuristic: the road / path / crossing materials in this project use dark/gray RGB
 * with low saturation. We treat very-low-saturation samples in the dark-mid range as
 * "on road" and skip dirt emission.
 *
 * In practice the cover texture isn't painted over roads (roads are separate meshes drawn
 * on top of the terrain), so the sample we get IS the terrain underneath the road. We
 * detect "on road" instead by checking if the road mesh is between us and the camera —
 * but that's expensive. For v1, we just always emit when wheel is grounded + spinning;
 * if it looks too noisy on roads, we'll add a proper road-surface check.
 *
 * For now this function returns false so dirt emits whenever the wheel is moving, with
 * particles tinted to the terrain cover (correct color even when "on road").
 */
function isRoadColor(_cover: [number, number, number]): boolean {
  return false;
}
